import { ReactNode } from 'react';
import { Link } from 'react-router';
import { DataGridRow } from './dataGridSchema';

export interface IDataGridCellDeps {
    /** Префикс ссылки на документ (`/document`); без него ячейки не ссылаются. */
    linkPrefix?: string;
    /** Разрешение схемы на ссылки (`options.urlEnabled`). */
    urlEnabled?: boolean;
    /** Класс переноса текста в ячейке (`no-wrap` / `trim-long-text` / `wrap`). */
    wrapClassName?: string;
    /** Режим просмотра: без hover-карточек контактов. */
    readOnly?: boolean;
    /** Шаблоны ячеек по имени из схемы (`dxGridCellTemplate`): кнопки скачивания,
     *  индикаторы и прочий доменный визуал, который знает только потребитель. */
    cellTemplates?: Record<string, (row: DataGridRow) => ReactNode>;
}

/**
 * Идентификатор документа строки: ключ приходит как «поток/ид», ссылку строим по
 * второй части. `-1` означает «документа нет» — такие строки не ссылаются.
 */
export const linkedDocIdOf = (row: DataGridRow): string => {
    const key = String((row as { documentKey?: string }).documentKey || row.key || '');
    const parts = key.split('/');
    return parts.length > 1 ? parts[1] : parts[0];
};

const linkable = (row: DataGridRow, deps: IDataGridCellDeps): boolean =>
    !!deps.linkPrefix && !!deps.urlEnabled && linkedDocIdOf(row) !== '-1';

/** Адрес документа строки по тем же правилам, что используются ссылками ячеек. */
export const linkedDocHrefOf = (row: DataGridRow, deps: IDataGridCellDeps): string | undefined =>
    linkable(row, deps) ? `${deps.linkPrefix}/${linkedDocIdOf(row)}` : undefined;

/** Оборачивает содержимое ячейки ссылкой на документ строки, если схема разрешила. */
export const withLink = (row: DataGridRow, deps: IDataGridCellDeps, content: ReactNode): ReactNode => {
    const href = linkedDocHrefOf(row, deps);
    return href ? <Link to={href}>{content}</Link> : content;
};
